import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  useConfirmImport,
  useImportBook,
  type ImportPreviewDto,
} from '../../api/queries/imports';

interface Props {
  open: boolean;
  onClose: () => void;
  bookId: string;
}

export function ImportDialog({ open, onClose, bookId }: Props) {
  const preview = useImportBook(bookId);
  const confirm = useConfirmImport(bookId);
  const [previewData, setPreviewData] = useState<ImportPreviewDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    try {
      const result = await preview.mutateAsync(file);
      setPreviewData(result);
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const handleConfirm = async () => {
    if (!previewData) return;
    try {
      await confirm.mutateAsync(previewData.importId);
      setPreviewData(null);
      onClose();
    } catch (ex) {
      setError((ex as Error).message);
    }
  };

  const handleCancel = () => {
    setPreviewData(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
      <DialogTitle>Import manuscript</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Upload a DOCX, TXT, or EPUB file. We'll parse it and let you review
            the chapter layout before committing.
          </Typography>

          {!previewData && (
            <Button
              variant="contained"
              component="label"
              disabled={preview.isPending}
            >
              {preview.isPending ? 'Parsing…' : 'Choose file'}
              <input
                hidden
                type="file"
                accept=".docx,.txt,.epub,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/epub+zip"
                onChange={handleFileChange}
              />
            </Button>
          )}

          {preview.isPending && <LinearProgress />}

          {previewData && (
            <>
              <Alert severity="success">
                Parsed {previewData.chapterCount} chapter
                {previewData.chapterCount === 1 ? '' : 's'} /{' '}
                {previewData.paragraphCount} paragraph
                {previewData.paragraphCount === 1 ? '' : 's'}.
              </Alert>
              <List dense>
                {previewData.chapters.map((c) => (
                  <ListItem key={c.sequenceOrder}>
                    <ListItemText
                      primary={c.title}
                      secondary={`${c.paragraphCount} paragraphs`}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!previewData || confirm.isPending}
        >
          {confirm.isPending ? 'Committing…' : 'Confirm import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
